import {
  type CursorMove,
  type CursorReactionCooldown,
  type CursorView,
  type CursorWireState,
  decodeCursorFrame,
  getCursorRegion,
  getCursorView,
} from "@sc07-canvas/lib/net";
import { RendererContext } from "@sc07-canvas/lib/renderer";
import throttle from "lodash.throttle";
import {
  type CSSProperties,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import reactionAwe from "../assets/reactions/awe.png";
import reactionBongo from "../assets/reactions/bongo.gif";
import reactionCry from "../assets/reactions/cry.png";
import reactionCute from "../assets/reactions/cute.png";
import reactionJam from "../assets/reactions/jam.gif";
import reactionLove from "../assets/reactions/love.png";
import reactionPog from "../assets/reactions/pog.png";
import reactionShocked from "../assets/reactions/shocked.png";
import reactionWork from "../assets/reactions/work.png";
import reactionYay from "../assets/reactions/yay.png";
import { useAppContext } from "../contexts/AppContext";
import { CanvasCore } from "../lib/canvas";
import { KeybindManager, REACTION_KEYBINDS } from "../lib/keybinds";
import Network from "../lib/network";
import {
  colorToCss,
  outlineForColor,
  RemoteCursorRenderer,
} from "./RemoteCursorRenderer";

const REACTION_DURATION = 1800;
const REACTION_COOLDOWN_DURATION = 1000;
const REACTION_COOLDOWN_MESSAGE = "You're on reaction cooldown";
const PLACEMENT_ANIMATION_DURATION = 280;
const PLACEMENT_ERROR_DURATION = 650;
const placementTimers = new WeakMap<HTMLElement, number>();
const REACTIONS = [
  reactionLove,
  reactionJam,
  reactionCute,
  reactionPog,
  reactionYay,
  reactionBongo,
  reactionShocked,
  reactionWork,
  reactionCry,
  reactionAwe,
];

const cursorStyle = (
  color: number | undefined,
  secondaryColor: number | undefined,
): CSSProperties =>
  ({
    "--cursor-primary": colorToCss(color),
    "--cursor-secondary": colorToCss(secondaryColor),
    "--cursor-outline": outlineForColor(color),
    "--cursor-placement-duration": PLACEMENT_ANIMATION_DURATION + "ms",
    "--cursor-reaction-duration": REACTION_DURATION + "ms",
  }) as CSSProperties;

const localCursorCss = (
  color: number | undefined,
  secondaryColor: number | undefined,
  outlineColor: string,
) => {
  const primary = colorToCss(color);
  const secondary = colorToCss(secondaryColor);
  const secondarySwatch =
    typeof secondaryColor === "number"
      ? `<rect x="11" y="15" width="6" height="6" rx=".75" fill="${secondary}" stroke="${outlineColor}" stroke-width=".65"/>`
      : "";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="22" viewBox="0 0 18 22"><path d="M1 1v15l4-4 3 7 2.5-1.2-3-6.6H13L1 1Z" fill="${primary}" stroke="${outlineColor}" stroke-width=".7" stroke-linejoin="round"/>${secondarySwatch}</svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 1 1, auto`;
};

const plainCursorCss = (color: number | undefined, outlineColor: string) => {
  const primary = colorToCss(color);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="22" viewBox="0 0 18 22"><path d="M1 1v15l4-4 3 7 2.5-1.2-3-6.6H13L1 1Z" fill="${primary}" stroke="${outlineColor}" stroke-width=".7" stroke-linejoin="round"/></svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 1 1, auto`;
};

const pointerCursorCss = (color: number | undefined, outlineColor: string) => {
  const primary = colorToCss(color);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="22" viewBox="0 0 18 22"><path d="M6 20c-1-3-4-5-4-7 0-1 1-2 2-1l2 2V4c0-1.3 2-1.3 2 0v6-2c0-1.2 2-1.2 2 0v2-1c0-1.2 2-1.2 2 0v1c0-1.2 2-1.2 2 0v4c0 3-2 6-4 6H6Z" fill="${primary}" stroke="${outlineColor}" stroke-width=".7" stroke-linejoin="round"/></svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 6 3, pointer`;
};

const textCursorCss = (color: number | undefined, outlineColor: string) => {
  const primary = colorToCss(color);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="22" viewBox="0 0 18 22"><path d="M6 2h6M9 2v18M6 20h6" fill="none" stroke="${outlineColor}" stroke-width="2.2"/><path d="M6 2h6M9 2v18M6 20h6" fill="none" stroke="${primary}" stroke-width=".9"/></svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 9 11, text`;
};

const disabledCursorCss = (color: number | undefined, outlineColor: string) => {
  const primary = colorToCss(color);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="22" viewBox="0 0 18 22"><circle cx="9" cy="11" r="6" fill="none" stroke="${outlineColor}" stroke-width="2.2"/><path d="m5 7 8 8" stroke="${outlineColor}" stroke-width="2.2"/><circle cx="9" cy="11" r="6" fill="none" stroke="${primary}" stroke-width=".8"/><path d="m5 7 8 8" stroke="${primary}" stroke-width=".8"/></svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 9 11, not-allowed`;
};

const UI_CURSOR_PROPERTIES = [
  "--collaborative-cursor-default",
  "--collaborative-cursor-pointer",
  "--collaborative-cursor-text",
  "--collaborative-cursor-disabled",
] as const;

const applyUiCursors = (color: number | undefined, outlineColor: string) => {
  document.body.classList.add("collaborative-cursors-active");
  document.body.style.setProperty(
    UI_CURSOR_PROPERTIES[0],
    plainCursorCss(color, outlineColor),
  );
  document.body.style.setProperty(
    UI_CURSOR_PROPERTIES[1],
    pointerCursorCss(color, outlineColor),
  );
  document.body.style.setProperty(
    UI_CURSOR_PROPERTIES[2],
    textCursorCss(color, outlineColor),
  );
  document.body.style.setProperty(
    UI_CURSOR_PROPERTIES[3],
    disabledCursorCss(color, outlineColor),
  );
};

const clearUiCursors = () => {
  document.body.classList.remove("collaborative-cursors-active");
  for (const property of UI_CURSOR_PROPERTIES) {
    document.body.style.removeProperty(property);
  }
};

const appendReaction = (
  root: HTMLElement | null,
  reaction: number,
  reduceMotion = false,
) => {
  const layer = root?.querySelector<HTMLElement>(
    ".collaborative-cursor-reactions",
  );
  const content = REACTIONS[reaction];
  if (!layer || !content) return;

  const element = document.createElement("img");
  element.alt = "";
  element.className = "collaborative-cursor-reaction";
  element.draggable = false;
  element.src = content;
  if (reduceMotion) element.classList.add("reduce-motion");
  layer.appendChild(element);
  window.setTimeout(() => element.remove(), REACTION_DURATION);
};

const animateCursorPlacement = (element: HTMLElement | null) => {
  if (!element) return;

  const previousTimer = placementTimers.get(element);
  if (previousTimer !== undefined) window.clearTimeout(previousTimer);

  element.classList.remove("is-placing");
  void element.offsetWidth;
  element.classList.add("is-placing");

  placementTimers.set(
    element,
    window.setTimeout(() => {
      element.classList.remove("is-placing");
      placementTimers.delete(element);
    }, PLACEMENT_ANIMATION_DURATION),
  );
};

const CursorVisual = ({
  cursor,
  className,
  elementRef,
}: {
  cursor: { color?: number; secondaryColor?: number };
  className: string;
  elementRef: (element: HTMLDivElement | null) => void;
}) => (
  <div
    ref={elementRef}
    className={className}
    style={cursorStyle(cursor.color, cursor.secondaryColor)}
    aria-hidden="true"
  >
    <div className="collaborative-cursor-visual">
      <svg
        className="collaborative-cursor-arrow"
        viewBox="0 0 18 22"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1 1v15l4-4 3 7 2.5-1.2-3-6.6H13L1 1Z"
          fill="var(--cursor-primary, #fff)"
          stroke="var(--cursor-outline)"
          strokeLinejoin="round"
          strokeWidth="0.7"
        />
        {typeof cursor.secondaryColor === "number" && (
          <rect
            className="collaborative-cursor-secondary"
            x="11"
            y="15"
            width="6"
            height="6"
            rx="0.75"
            fill="var(--cursor-secondary, #fff)"
            stroke="var(--cursor-outline)"
            strokeWidth="0.65"
          />
        )}
      </svg>
      <svg
        className="collaborative-cursor-error"
        viewBox="0 0 14 14"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1 2.5 2.5 1 7 5.5 11.5 1 13 2.5 8.5 7 13 11.5 11.5 13 7 8.5 2.5 13 1 11.5 5.5 7Z"
          fill="var(--cursor-primary, #fff)"
          stroke="var(--cursor-outline)"
          strokeLinejoin="round"
          strokeWidth="0.65"
        />
      </svg>
      <span className="collaborative-cursor-reactions" />
      <span className="collaborative-cursor-reaction-cooldown">
        <span className="collaborative-cursor-reaction-cooldown-label">
          On cooldown...
        </span>
        <span className="collaborative-cursor-reaction-cooldown-track">
          <span className="collaborative-cursor-reaction-cooldown-fill" />
        </span>
      </span>
    </div>
  </div>
);

const wireIsInView = (wire: CursorWireState, view: CursorView) => {
  const region = getCursorRegion(view.regionSize, wire[2], wire[3]);
  return (
    region.x >= view.minRegionX &&
    region.x <= view.maxRegionX &&
    region.y >= view.minRegionY &&
    region.y <= view.maxRegionY
  );
};

export const CursorPresenceLayer = () => {
  const { user, cursor, settings } = useAppContext();
  const outlineColor = outlineForColor(cursor.color);
  const PanZoom = useContext(RendererContext);
  const enabled = settings.get["presence.cursors"];
  const reactionsEnabled = settings.get["presence.reactions"];
  const localElement = useRef<HTMLDivElement | null>(null);
  const cursorCanvas = useRef<HTMLCanvasElement | null>(null);
  const reactionLayer = useRef<HTMLDivElement | null>(null);
  const renderer = useRef<RemoteCursorRenderer | null>(null);
  const remoteData = useRef(new Map<number, CursorWireState>());
  const latestSequences = useRef(new Map<number, number>());
  const actionSequences = useRef(new Map<number, number>());
  const selfId = useRef<number | undefined>(undefined);
  const activeView = useRef<CursorView | undefined>(undefined);
  const colorRef = useRef(cursor.color);
  const secondaryColorRef = useRef(cursor.secondaryColor);
  const outlineColorRef = useRef(outlineColor);
  const reactionsEnabledRef = useRef(reactionsEnabled);
  const localActive = useRef(false);
  const lastPresence = useRef<CursorMove | null>(null);
  const requestViewportRef = useRef<(force?: boolean) => void>(() => {});

  colorRef.current = cursor.color;
  secondaryColorRef.current = cursor.secondaryColor;
  outlineColorRef.current = outlineColor;
  reactionsEnabledRef.current = reactionsEnabled;

  useEffect(() => {
    if (!enabled || !user || !reactionsEnabled) return;
    for (const source of REACTIONS) {
      const image = new Image();
      image.src = source;
    }
  }, [enabled, reactionsEnabled, user]);

  useEffect(() => {
    if (!enabled || !user) return;
    if (!reactionsEnabled) {
      document
        .querySelectorAll(".collaborative-cursor-reaction")
        .forEach((reaction) => reaction.remove());
      renderer.current?.clearReactions();
      localElement.current
        ?.querySelector(".collaborative-cursor-reaction-cooldown")
        ?.classList.remove("is-active");
    }
    requestViewportRef.current(true);
  }, [enabled, reactionsEnabled, user]);

  useEffect(() => {
    const wrapper = document.querySelector<HTMLElement>(".board-wrapper");
    const canvas = CanvasCore.get(false);
    const socket = Network.socket;
    const surface = cursorCanvas.current;

    if (!enabled || !user || !wrapper || !canvas || !surface) {
      localElement.current?.style.setProperty("display", "none");
      wrapper?.style.removeProperty("cursor");
      clearUiCursors();
      renderer.current?.dispose();
      renderer.current = null;
      remoteData.current.clear();
      latestSequences.current.clear();
      actionSequences.current.clear();
      return;
    }

    const remoteRenderer = new RemoteCursorRenderer(surface, canvas);
    renderer.current = remoteRenderer;
    remoteRenderer.setLocalZoom(PanZoom.transform.scale);
    wrapper.style.cursor = localCursorCss(
      colorRef.current,
      secondaryColorRef.current,
      outlineColorRef.current,
    );
    applyUiCursors(colorRef.current, outlineColorRef.current);

    let mounted = true;
    let visualFrame: number | undefined;
    let viewportFrame: number | undefined;
    let settleTimer: number | undefined;
    let viewportRetryTimer: number | undefined;
    let viewportRequest = 0;
    let viewportSignature = "";
    let lastReactionAt = -REACTION_COOLDOWN_DURATION;
    let reactionBlockedUntil = 0;
    let reactionBlockedCooldown: CursorReactionCooldown | undefined;
    let reactionCooldownTimer: number | undefined;
    let pendingVisualPosition: { x: number; y: number } | undefined;

    const sendCursor = throttle(
      (presence: CursorMove) => {
        socket.volatile.emit("cursorMove", presence);
      },
      50,
      { leading: true, trailing: true },
    );
    let lastPublishedZoom = PanZoom.transform.scale;
    const sendZoom = throttle(
      (zoom: number) => {
        if (socket.connected) socket.emit("cursorZoom", zoom);
      },
      200,
      { leading: true, trailing: true },
    );

    const drawLocalCursor = () => {
      visualFrame = undefined;
      if (!pendingVisualPosition) return;
      const element = localElement.current;
      if (!element) return;
      element.style.display = "block";
      element.style.transform = `translate3d(${pendingVisualPosition.x}px, ${pendingVisualPosition.y}px, 0)`;
    };

    const queueLocalCursor = (x: number, y: number) => {
      pendingVisualPosition = { x, y };
      if (visualFrame === undefined) {
        visualFrame = requestAnimationFrame(drawLocalCursor);
      }
    };

    const clearSettleTimer = () => {
      if (settleTimer === undefined) return;
      window.clearTimeout(settleTimer);
      settleTimer = undefined;
    };

    const scheduleSettle = () => {
      clearSettleTimer();
      settleTimer = window.setTimeout(() => {
        settleTimer = undefined;
        if (lastPresence.current) {
          socket.emit("cursorSettle", lastPresence.current);
        }
      }, 120);
    };

    const publishPresence = (
      clientX: number,
      clientY: number,
      x: number,
      y: number,
    ) => {
      queueLocalCursor(clientX, clientY);
      const presence: CursorMove = {
        x,
        y,
        color: colorRef.current,
        zoom: PanZoom.transform.scale,
      };
      localActive.current = true;
      lastPresence.current = presence;
      sendCursor(presence);
      scheduleSettle();
    };

    const clearLocalPresence = () => {
      if (!localActive.current) return;
      localActive.current = false;
      lastPresence.current = null;
      sendCursor.cancel();
      clearSettleTimer();
      socket.emit("cursorHide");
    };

    const hideLocalCursor = () => {
      pendingVisualPosition = undefined;
      if (visualFrame !== undefined) {
        cancelAnimationFrame(visualFrame);
        visualFrame = undefined;
      }
      localElement.current?.style.setProperty("display", "none");
      clearLocalPresence();
    };

    const handleSurfacePointer = ({
      active,
      clientX,
      clientY,
      x,
      y,
    }: {
      active: boolean;
      clientX: number;
      clientY: number;
      x?: number;
      y?: number;
    }) => {
      if (!active || x === undefined || y === undefined) {
        const hasActiveReaction = Boolean(
          localElement.current?.querySelector(".collaborative-cursor-reaction"),
        );
        if (hasActiveReaction) {
          queueLocalCursor(clientX, clientY);
          clearLocalPresence();
        } else {
          hideLocalCursor();
        }
        return;
      }
      publishPresence(clientX, clientY, x, y);
    };

    const applyFrame = (frame: ArrayBuffer) => {
      const decoded = decodeCursorFrame(frame);
      if (!decoded) return;
      const receivedIds = new Set<number>();

      for (const wire of decoded.cursors) {
        if (wire[0] === selfId.current) continue;
        if (activeView.current && !wireIsInView(wire, activeView.current)) {
          continue;
        }
        const latest = latestSequences.current.get(wire[0]) ?? -1;
        if (wire[1] < latest) continue;
        latestSequences.current.set(wire[0], wire[1]);
        remoteData.current.set(wire[0], wire);
        receivedIds.add(wire[0]);
        remoteRenderer.apply(wire);
      }

      if (decoded.keyframe) {
        for (const [id, wire] of remoteData.current) {
          if (
            id !== selfId.current &&
            activeView.current &&
            wireIsInView(wire, activeView.current) &&
            !receivedIds.has(id)
          ) {
            remoteData.current.delete(id);
            remoteRenderer.remove(id);
          }
        }
      }
    };

    const handleRemove = (removals: Array<[number, number]>) => {
      for (const [id, sequence] of removals) {
        const latest = latestSequences.current.get(id) ?? -1;
        if (sequence < latest) continue;
        latestSequences.current.set(id, sequence);
        remoteData.current.delete(id);
        remoteRenderer.remove(id);
      }
    };

    const acceptAction = (id: number, sequence: number) => {
      if (id === selfId.current) return false;
      const latest = actionSequences.current.get(id) ?? 0;
      if (sequence <= latest) return false;
      actionSequences.current.set(id, sequence);
      return true;
    };

    const handleReaction = ({
      id,
      reaction,
      sequence,
    }: {
      id: number;
      reaction: number;
      sequence: number;
    }) => {
      if (
        !reactionsEnabledRef.current ||
        !remoteData.current.has(id) ||
        !acceptAction(id, sequence)
      ) {
        return;
      }
      const root = reactionLayer.current;
      if (!root) return;
      const anchor = document.createElement("div");
      anchor.className = "collaborative-cursor-reaction-anchor";
      anchor.style.setProperty(
        "--cursor-reaction-duration",
        REACTION_DURATION + "ms",
      );
      const reactions = document.createElement("span");
      reactions.className = "collaborative-cursor-reactions";
      anchor.appendChild(reactions);
      root.appendChild(anchor);
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      appendReaction(anchor, reaction, reduceMotion);
      remoteRenderer.attachReaction(id, anchor, REACTION_DURATION);
    };

    const handlePlace = ({
      id,
      sequence,
    }: {
      id: number;
      sequence: number;
    }) => {
      if (!acceptAction(id, sequence)) return;
      remoteRenderer.place(id);
    };

    const calculateViewport = () => {
      const viewportWrapper = PanZoom.$wrapper;
      if (!mounted || !canvas.isSurfaceReady() || !viewportWrapper) return;
      const rect = viewportWrapper.getBoundingClientRect();
      const [left, top] = canvas.screenToPos(rect.left - 32, rect.top - 32);
      const [right, bottom] = canvas.screenToPos(
        rect.right + 32,
        rect.bottom + 32,
      );
      return {
        left,
        top,
        right,
        bottom,
        zoom: PanZoom.transform.scale,
        reactions: reactionsEnabledRef.current,
      };
    };

    const requestViewport = (force = false) => {
      if (!mounted || !socket.connected || !canvas.isSurfaceReady()) return;
      const viewport = calculateViewport();
      if (!viewport) return;
      const view = getCursorView(viewport);
      if (!view) return;
      const signature = [
        view.regionSize,
        view.minRegionX,
        view.maxRegionX,
        view.minRegionY,
        view.maxRegionY,
        viewport.reactions ? 1 : 0,
      ].join(":");
      if (!force && signature === viewportSignature) return;

      viewportSignature = signature;
      activeView.current = view;
      const request = ++viewportRequest;
      if (viewportRetryTimer !== undefined) {
        window.clearTimeout(viewportRetryTimer);
      }
      viewportRetryTimer = window.setTimeout(() => {
        if (!mounted || request !== viewportRequest) return;
        viewportSignature = "";
        requestViewport(true);
      }, 1500);

      socket.emit("cursorViewport", viewport, (snapshot) => {
        if (!mounted || request !== viewportRequest) return;
        if (viewportRetryTimer !== undefined) {
          window.clearTimeout(viewportRetryTimer);
          viewportRetryTimer = undefined;
        }
        if (!snapshot) {
          viewportSignature = "";
          viewportRetryTimer = window.setTimeout(() => {
            if (mounted) requestViewport(true);
          }, 250);
          return;
        }

        selfId.current = snapshot.selfId;
        activeView.current = snapshot.view;
        remoteData.current.delete(snapshot.selfId);
        remoteRenderer.remove(snapshot.selfId);
        applyFrame(snapshot.frame);

        for (const [id, wire] of remoteData.current) {
          if (!wireIsInView(wire, snapshot.view)) {
            remoteData.current.delete(id);
            remoteRenderer.remove(id);
          }
        }
      });
    };
    requestViewportRef.current = requestViewport;

    const scheduleViewport = () => {
      if (viewportFrame !== undefined) return;
      viewportFrame = requestAnimationFrame(() => {
        viewportFrame = undefined;
        if (!mounted || !canvas.isSurfaceReady()) return;
        requestViewport();
      });
    };

    const handleViewportMove = ({ scale }: { scale: number }) => {
      if (!mounted || !canvas.isSurfaceReady()) return;
      remoteRenderer.setLocalZoom(scale);
      if (scale !== lastPublishedZoom) {
        lastPublishedZoom = scale;
        sendZoom(scale);
      }
      scheduleViewport();
    };
    const viewportTransformObserver = new MutationObserver(() => {
      handleViewportMove({ scale: PanZoom.transform.scale });
    });
    const observeViewportTransform = () => {
      const element = PanZoom.$move;
      if (!element) return;

      viewportTransformObserver.disconnect();
      viewportTransformObserver.observe(element, {
        attributes: true,
        attributeFilter: ["style"],
      });
    };
    observeViewportTransform();

    const handleInitialize = () => {
      observeViewportTransform();
      handleViewportMove({ scale: PanZoom.transform.scale });
      requestViewport(true);
    };

    const handleConnect = () => {
      viewportSignature = "";
      requestViewport(true);
    };

    const handleDisconnect = () => {
      selfId.current = undefined;
      activeView.current = undefined;
      remoteData.current.clear();
      latestSequences.current.clear();
      actionSequences.current.clear();
      remoteRenderer.clear();
    };

    const showReactionCooldown = (cooldown: CursorReactionCooldown) => {
      if (cooldown.until <= Date.now()) return;
      reactionBlockedUntil = Math.max(reactionBlockedUntil, cooldown.until);
      if (cooldown.level === "second") return;

      reactionBlockedCooldown = cooldown;
      const bar = localElement.current?.querySelector<HTMLElement>(
        ".collaborative-cursor-reaction-cooldown",
      );
      if (!bar) return;
      if (reactionCooldownTimer !== undefined) {
        window.clearTimeout(reactionCooldownTimer);
      }
      bar.classList.remove("is-active");
      void bar.offsetWidth;
      bar.classList.add("is-active");
      reactionCooldownTimer = window.setTimeout(() => {
        bar.classList.remove("is-active");
        reactionCooldownTimer = undefined;
      }, REACTION_DURATION + 50);
    };

    const sendReaction = (reaction: number) => {
      if (
        !reactionsEnabledRef.current ||
        !localActive.current ||
        !socket.connected
      ) {
        return;
      }

      const now = performance.now();
      if (Date.now() < reactionBlockedUntil) {
        if (reactionBlockedCooldown) {
          showReactionCooldown(reactionBlockedCooldown);
        }
        toast.error(REACTION_COOLDOWN_MESSAGE);
        return;
      }
      reactionBlockedCooldown = undefined;
      if (now - lastReactionAt < REACTION_COOLDOWN_DURATION) {
        toast.error(REACTION_COOLDOWN_MESSAGE);
        return;
      }

      lastReactionAt = now;
      socket.emit("cursorReaction", reaction, (result) => {
        if (!mounted) return;
        if (!result.success) {
          if (result.cooldown) {
            showReactionCooldown(result.cooldown);
            toast.error(REACTION_COOLDOWN_MESSAGE);
          }
          return;
        }

        const reduceMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;
        appendReaction(localElement.current, reaction, reduceMotion);
      });
    };
    const reactionKeybindHandlers = REACTION_KEYBINDS.map(
      (keybind, reaction) => {
        const handle = () => sendReaction(reaction);
        KeybindManager.on(keybind, handle);
        return [keybind, handle] as const;
      },
    );

    const handleResize = () => {
      remoteRenderer.resize();
      scheduleViewport();
    };

    socket.on("cursorFrame", applyFrame);
    socket.on("cursorRemove", handleRemove);
    socket.on("cursorReaction", handleReaction);
    socket.on("cursorPlace", handlePlace);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    canvas.on("surfacePointer", handleSurfacePointer);
    PanZoom.on("viewportMove", handleViewportMove);
    PanZoom.on("initialize", handleInitialize);
    window.addEventListener("blur", hideLocalCursor);
    window.addEventListener("resize", handleResize);

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(PanZoom.$wrapper || wrapper);
    handleViewportMove({ scale: PanZoom.transform.scale });
    if (socket.connected) requestViewport(true);

    return () => {
      mounted = false;
      requestViewportRef.current = () => {};
      sendCursor.cancel();
      sendZoom.cancel();
      clearSettleTimer();
      if (reactionCooldownTimer !== undefined) {
        window.clearTimeout(reactionCooldownTimer);
      }
      localElement.current
        ?.querySelector(".collaborative-cursor-reaction-cooldown")
        ?.classList.remove("is-active");
      if (visualFrame !== undefined) cancelAnimationFrame(visualFrame);
      if (viewportFrame !== undefined) cancelAnimationFrame(viewportFrame);
      if (viewportRetryTimer !== undefined) {
        window.clearTimeout(viewportRetryTimer);
      }
      resizeObserver.disconnect();
      viewportTransformObserver.disconnect();
      socket.emit("cursorUnsubscribe");
      socket.off("cursorFrame", applyFrame);
      socket.off("cursorRemove", handleRemove);
      socket.off("cursorReaction", handleReaction);
      socket.off("cursorPlace", handlePlace);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      canvas.off("surfacePointer", handleSurfacePointer);
      PanZoom.off("viewportMove", handleViewportMove);
      PanZoom.off("initialize", handleInitialize);
      for (const [keybind, handle] of reactionKeybindHandlers) {
        KeybindManager.off(keybind, handle);
      }
      window.removeEventListener("blur", hideLocalCursor);
      window.removeEventListener("resize", handleResize);
      localActive.current = false;
      lastPresence.current = null;
      localElement.current?.style.setProperty("display", "none");
      wrapper.style.removeProperty("cursor");
      clearUiCursors();
      selfId.current = undefined;
      activeView.current = undefined;
      remoteData.current.clear();
      latestSequences.current.clear();
      actionSequences.current.clear();
      remoteRenderer.dispose();
      if (renderer.current === remoteRenderer) renderer.current = null;
    };
  }, [enabled, PanZoom, user]);

  useEffect(() => {
    if (!enabled || !user) return;

    let restoreTimer: number | undefined;
    const restoreCursor = () => {
      localElement.current?.classList.remove("is-error");
      const wrapper = document.querySelector<HTMLElement>(".board-wrapper");
      if (!wrapper) return;
      wrapper.style.cursor = localCursorCss(
        colorRef.current,
        secondaryColorRef.current,
        outlineColorRef.current,
      );
    };
    const scheduleRestore = (duration: number) => {
      if (restoreTimer !== undefined) window.clearTimeout(restoreTimer);
      restoreTimer = window.setTimeout(restoreCursor, duration);
    };
    const handlePlaceSuccessful = () => {
      if (
        !localActive.current ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        return;
      }

      const wrapper = document.querySelector<HTMLElement>(".board-wrapper");
      if (!wrapper) return;
      localElement.current?.classList.remove("is-error");
      wrapper.style.cursor = "none";
      animateCursorPlacement(localElement.current);
      scheduleRestore(PLACEMENT_ANIMATION_DURATION);
    };
    const handlePlaceFailed = () => {
      if (!localActive.current) return;
      const wrapper = document.querySelector<HTMLElement>(".board-wrapper");
      const element = localElement.current;
      if (!wrapper || !element) return;
      element.classList.remove("is-placing");
      element.classList.add("is-error");
      wrapper.style.cursor = "none";
      scheduleRestore(PLACEMENT_ERROR_DURATION);
    };

    Network.on("placeSuccessful", handlePlaceSuccessful);
    Network.on("placeFailed", handlePlaceFailed);
    return () => {
      if (restoreTimer !== undefined) window.clearTimeout(restoreTimer);
      localElement.current?.classList.remove("is-placing", "is-error");
      Network.off("placeSuccessful", handlePlaceSuccessful);
      Network.off("placeFailed", handlePlaceFailed);
    };
  }, [enabled, user]);

  useLayoutEffect(() => {
    const wrapper = document.querySelector<HTMLElement>(".board-wrapper");
    if (enabled && user && wrapper) {
      wrapper.style.cursor = localCursorCss(
        cursor.color,
        cursor.secondaryColor,
        outlineColor,
      );
      applyUiCursors(cursor.color, outlineColor);
    } else {
      wrapper?.style.removeProperty("cursor");
      clearUiCursors();
    }

    if (enabled && user && localActive.current && lastPresence.current) {
      const presence = {
        ...lastPresence.current,
        color: cursor.color,
      };
      lastPresence.current = presence;
      Network.socket.emit("cursorSettle", presence);
    }
  }, [cursor.color, cursor.secondaryColor, enabled, outlineColor, user]);

  return createPortal(
    <>
      <canvas
        ref={cursorCanvas}
        className="collaborative-cursor-canvas"
        aria-hidden="true"
      />
      <div
        ref={reactionLayer}
        className="collaborative-cursor-reaction-layer"
        aria-hidden="true"
      />
      <CursorVisual
        cursor={cursor}
        className="collaborative-cursor collaborative-cursor--local"
        elementRef={(element) => {
          localElement.current = element;
        }}
      />
    </>,
    document.body,
  );
};
